import {
  Grid,
  GridColumn as Column,
  GridColumnMenuCheckboxFilter,
  GridColumnMenuFilter,
} from "@progress/kendo-react-grid";
import * as React from "react";
import { Button } from "@progress/kendo-react-buttons";
import { process } from "@progress/kendo-data-query";
import { useQueryClient } from "react-query";

import { QUERY_KEYS } from "../../data/helpers/queryKeys";
import LoadingIndicator from "../../components/LoadingIndicator";
import {
  RateGroupMappingSuccess,
  RateGroupMappingInProgress,
} from "./ImportStatusHelper";
import {
  useQueryGetImportRateGroupMapping,
  useMutatePostRetryMapping,
} from "../../data/services/importService";

const ImportBillingRateMapping = ({
  id,
  importItem,
  onNextClick,
  onPrevClick,
}) => {
  const [importRGMappingSrc, setimportRGMappingSrc] = React.useState([]);
  const [importRGMappingMaster, setImportRGMappingMaster] = React.useState([]);
  const [gridDataState, setGridDataState] = React.useState({
    take: 20,
    skip: 0,
  });

  const [nextEnabled, setNextEnabled] = React.useState(false);
  const [isMappingInProgress, setIsMappingInProgress] = React.useState(false);
  const queryClient = useQueryClient();

  const pagerSetting = {
    buttonCount: 5,
    info: true,
    type: "numeric",
    pageSizes: [10, 20, 50, 100, 200, 500],
    previousNext: true,
  };

  const { isLoading: importRGMappingLoading, data: importRGMapping } =
    useQueryGetImportRateGroupMapping(id);
  const useMutatePostRetryMappingHandler = useMutatePostRetryMapping();

  React.useEffect(() => {
    let rgMappingSuccess =
      importItem && RateGroupMappingSuccess(importItem.importStatus);
    let rgMappingInProgress =
      importItem && RateGroupMappingInProgress(importItem.importStatus);

    if (!importRGMappingLoading && importRGMapping && importRGMapping.data) {
      if (rgMappingSuccess) {
        setImportRGMappingMaster(importRGMapping.data);
        setimportRGMappingSrc(
          process(importRGMapping.data.slice(0), gridDataState)
        );
      } else {
        let filtered = importRGMapping.data.filter((x) => !x.rateGroup);
        setImportRGMappingMaster(filtered);
        setimportRGMappingSrc(process(filtered.slice(0), gridDataState));
      }
    }

    setNextEnabled(rgMappingSuccess);
    setIsMappingInProgress(rgMappingInProgress);
  }, [importRGMappingLoading, importRGMapping, gridDataState, importItem]);

  const onRetryClick = async () => {
    var data = {
      importId: id,
      content: {
        importFileQueueId: id,
        requestType: "RateMapping",
      },
    };

    await useMutatePostRetryMappingHandler.mutateAsync(data);
  };

  const onRefreshClick = () => {
    queryClient.invalidateQueries([QUERY_KEYS.IMPORT_ITEM, id]);
  };

  const RenderGrid = () => {
    const dataStateChange = (event) => {
      setimportRGMappingSrc(
        process(importRGMappingMaster.slice(0), event.dataState)
      );
      setGridDataState(event.dataState);
    };

    const columnMenuHeader = (field) => {
      var active = GridColumnMenuFilter.active(field, gridDataState.filter);
      return active ? "active" : "";
    };

    return (
      <Grid
        pageable={pagerSetting}
        pageSize={20}
        sortable={true}
        data={importRGMappingSrc}
        {...gridDataState}
        onDataStateChange={dataStateChange}
      >
        <Column
          field="planGroup"
          title="Plan Group"
          columnMenu={ColumnCheckboxMenu}
          headerClassName={columnMenuHeader("planGroup")}
        />
        <Column
          field="rateFactorName"
          title="Rate Factor Name"
          columnMenu={ColumnCheckboxMenu}
          headerClassName={columnMenuHeader("rateFactorName")}
        />
        <Column
          field="rateFactor"
          title="Rate Factor"
          columnMenu={ColumnCheckboxMenu}
          headerClassName={columnMenuHeader("rateFactor")}
        />
        {nextEnabled && (
          <Column
            field="rateGroup"
            title="PrismHR Rate Group"
            columnMenu={ColumnCheckboxMenu}
            headerClassName={columnMenuHeader("rateGroup")}
          />
        )}
      </Grid>
    );
  };

  const ColumnCheckboxMenu = (props) => {
    return (
      <div>
        <GridColumnMenuCheckboxFilter
          {...props}
          data={importRGMappingMaster}
          expanded={true}
        />
      </div>
    );
  };

  if (importRGMappingLoading) {
    return <LoadingIndicator />;
  }

  return (
    <div>
      <div
        style={{
          justifyContent: "space-between",
          display: "flex",
          paddingBottom: 10,
        }}
      >
        <div style={{ alignSelf: "center" }}>
          <Button
            themeColor={"light"}
            onClick={onPrevClick}
            iconClass="k-icon k-i-arrow-left"
          >
            Plan Mapping
          </Button>
        </div>
        <div style={{ alignSelf: "center" }}>
          <h3 style={{ marginTop: 30 }}>PrismHR Rate Group Mapping</h3>
        </div>
        <div style={{ alignSelf: "center" }}>
          {nextEnabled && (
            <Button
              themeColor={"primary"}
              onClick={onNextClick}
              iconClass="k-icon k-i-arrow-right"
            >
              Apply Cost
            </Button>
          )}
          {!nextEnabled && (
            <Button
              themeColor={"primary"}
              onClick={onRetryClick}
              disabled={isMappingInProgress}
            >
              {" "}
              Retry{" "}
            </Button>
          )}
        </div>
      </div>
      {!nextEnabled && !isMappingInProgress && (
        <div
          style={{
            padding: 20,
            marginTop: 10,
            marginBottom: 20,
            backgroundColor: "#f8d7da",
            color: "#721c24",
            borderRadius: ".25rem",
          }}
        >
          Error mapping to PrismHR Billing Rate Group. Below is the list of Rate
          Factors that could not be mapped. Please setup the mapping in "PrismHR
          Mapping" tab and retry.
        </div>
      )}
      {isMappingInProgress && (
        <>
          <div
            style={{
              padding: 20,
              marginTop: 10,
              marginBottom: 20,
              backgroundColor: "#fff3cd",
              color: "#856404",
              borderRadius: ".25rem",
            }}
          >
            Mapping to PrismHR Billing Rate Group is in progress.{" "}
            <Button
              themeColor="info"
              onClick={onRefreshClick}
              style={{ marginLeft: 20 }}
            >
              {" "}
              Refresh{" "}
            </Button>
          </div>
        </>
      )}
      {!isMappingInProgress && <RenderGrid />}
    </div>
  );
};

export default ImportBillingRateMapping;
